import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { API_URL } from "../utils/api";
import LanguagePicker from "../components/LanguagePicker";
import PageMeta from "../components/PageMeta";
import SocialAuthButtons from "../components/SocialAuthButtons";
import BlurText from "../components/BlurText";
import Silk from "../components/Silk";
import Dither from "../components/Dither";
import { useIsMobile } from "../hooks/useIsMobile";

export default function Login() {
  const { lang, setLang, setToken, setUser } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Un visiteur venu d'une fiche publique ("Revendiquer ce lieu") ou de /pro ("Créer ma page")
  // porte son intention dans ce paramètre — voir RedirectToLogin dans App.tsx.
  const redirect = searchParams.get("redirect");
  const isMobile = useIsMobile();

  // Double authentification (voir Settings > Sécurité) : le mot de passe seul renvoie
  // requires_2fa + pending_token plutôt que des tokens — ce formulaire passe alors en étape 2
  // (code reçu par email) sans jamais redemander le mot de passe.
  const [pendingToken, setPendingToken] = useState("");
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, lang }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || t(lang, "error_credentials"));
      else if (data.requires_2fa) setPendingToken(data.pending_token);
      else { setToken(data.accessToken, data.refreshToken); setUser(data.user); navigate(redirect || "/"); }
    } catch { setError(t(lang, "error_connection")); }
    finally { setLoading(false); }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/auth/2fa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pending_token: pendingToken, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || t(lang, "error_connection"));
      else { setToken(data.accessToken, data.refreshToken); setUser(data.user); navigate(redirect || "/"); }
    } catch { setError(t(lang, "error_connection")); }
    finally { setLoading(false); }
  };

  const resendCode = async () => {
    if (resending) return;
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/auth/2fa/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pending_token: pendingToken, lang }),
      });
      const data = await res.json();
      if (res.ok) setPendingToken(data.pending_token);
    } catch {}
    setResending(false);
  };

  const desc = (t as any)(lang, "description") || t(lang, "login_sub");

  const handleForgot = async () => {
    if (!forgotEmail) return;
    try {
      await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
    } catch {
      // On affiche toujours le message de confirmation : on ne révèle pas si l'email existe
    }
    setForgotSent(true);
    setTimeout(() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }, 3000);
  };

  const fieldStyle: React.CSSProperties = {
    width: "100%", border: "1.5px solid #E0E0E0", borderRadius: 14,
    padding: "13px 16px", fontSize: 15, outline: "none",
    backgroundColor: "#FAFAFA", color: "#0a0a0a", transition: "border-color 0.15s",
  };
  const onFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => (e.currentTarget.style.borderColor = "#0a0a0a");
  const onFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => (e.currentTarget.style.borderColor = "#E0E0E0");

  return (
    <>
    <PageMeta title="Connexion" description="Connecte-toi à OLTODO pour rejoindre des activités, des communautés et des rencontres authentiques près de chez toi." />
    <div style={{ height: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 20, boxSizing: "border-box" }}>

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : le
          conteneur de cette page utilise WebkitOverflowScrolling:"touch", et sur Safari iOS
          un position:fixed descendant s'y positionne par rapport au contenu défilant plutôt
          qu'au vrai viewport. */}
      {showForgot && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ backgroundColor: "white", borderRadius: 24, padding: isMobile ? 28 : 40, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            {!forgotSent ? (
              <>
                <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{t(lang, "forgot")}</h3>
                <p style={{ fontSize: 14, color: "#999", marginBottom: 24, lineHeight: 1.6 }}>{t(lang, "forgot_desc")}</p>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder={t(lang, "email")}
                  onFocus={onFieldFocus} onBlur={onFieldBlur}
                  style={{ ...fieldStyle, marginBottom: 20 }} />
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setShowForgot(false)} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "1.5px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#666" }}>{t(lang, "cancel")}</button>
                  <button onClick={handleForgot} disabled={!forgotEmail} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "none", backgroundColor: forgotEmail ? "#0a0a0a" : "#E0E0E0", color: "white", cursor: forgotEmail ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 700 }}>{t(lang, "send")}</button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center" }}>
                <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{t(lang, "forgot_sent_title")}</h3>
                <p style={{ fontSize: 14, color: "#999", lineHeight: 1.6 }}>{t(lang, "forgot_sent_desc")}</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", width: "100%", maxWidth: 960, backgroundColor: "white", borderRadius: isMobile ? 20 : 28, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}>

        <div style={{
          position: "relative",
          flex: isMobile ? "none" : 1,
          backgroundColor: "#0a0a0a",
          padding: isMobile ? "32px 24px" : 52,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          overflow: "hidden",
        }}>
          {/* Silk (nappes de lumière qui dérivent) + Dither (grain qui respire par-dessus) —
              purement décoratif, en dessous du contenu par simple ordre du DOM (pas de z-index
              nécessaire). Les deux sont 100% noir/blanc/gris, aucune couleur ajoutée. */}
          <Silk />
          <Dither opacity={0.06} />
          <div style={{ position: "relative", marginBottom: isMobile ? 0 : 32 }}>
            <img src="/oltodo.png" alt="OLTODO" style={{ width: isMobile ? 52 : 80, height: isMobile ? 52 : 80, objectFit: "contain", display: "block", margin: isMobile ? "0 auto 10px" : "0 auto 16px" }} />
            <div style={{ color: "white", fontWeight: 900, fontSize: isMobile ? 26 : 36, letterSpacing: 4, marginBottom: 8 }}>OLTODO</div>
            {/* Seule l'accroche (une poignée de mots) reçoit le flou d'entrée — le wordmark
                OLTODO juste au-dessus reste net à l'instant T, une signature de marque ne doit
                jamais "papilloter" à l'affichage. */}
            <BlurText
              text="YOU WILL NEVER BE ALONE"
              style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? 9 : 11, letterSpacing: 3, textTransform: "uppercase" }}
            />
          </div>

          {!isMobile && (
            <p style={{ position: "relative", color: "rgba(255,255,255,0.7)", fontSize: 15, lineHeight: 1.8, maxWidth: 340 }}>
              <BlurText text={desc} delay={250} stagger={22} />
            </p>
          )}
        </div>

        <div style={{ position: "relative", flex: 1, padding: isMobile ? "28px 24px" : 52, display: "flex", flexDirection: "column", justifyContent: "center" }}>

          <div style={{ position: "absolute", top: isMobile ? 16 : 20, right: isMobile ? 16 : 20 }}>
            <LanguagePicker lang={lang} setLang={setLang} size={28} align="right" />
          </div>

          {pendingToken ? (
            <>
              <h3 style={{ fontSize: isMobile ? 24 : 28, fontWeight: 800, marginBottom: 6, color: "#0a0a0a", marginTop: isMobile ? 20 : 0 }}>{t(lang, "twofa_login_title")}</h3>
              <p style={{ color: "#999", marginBottom: 28, fontSize: 14 }}>{t(lang, "twofa_login_sub")}</p>

              {error && (
                <div style={{ backgroundColor: "#F5F5F5", border: "1px solid #E0E0E0", borderRadius: 12, padding: "12px 16px", color: "#0a0a0a", fontSize: 13, marginBottom: 20, fontWeight: 600 }}>
                  {error}
                </div>
              )}

              <form onSubmit={verifyCode}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>{t(lang, "twofa_code_label")}</label>
                  <input
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    style={{ ...fieldStyle, letterSpacing: 8, fontSize: 22, textAlign: "center" }}
                  />
                </div>

                <div style={{ textAlign: "right", marginBottom: 24 }}>
                  <span onClick={resendCode} style={{ color: "#0a0a0a", fontSize: 13, cursor: "pointer", fontWeight: 700, textDecoration: "underline" }}>
                    {resending ? t(lang, "sending_ellipsis") : t(lang, "twofa_resend_code")}
                  </span>
                </div>

                <button type="submit" disabled={loading || code.length < 6} style={{
                  width: "100%", backgroundColor: "#0a0a0a", color: "white",
                  borderRadius: 14, padding: "15px", fontSize: 15, fontWeight: 700,
                  border: "none", cursor: "pointer", opacity: (loading || code.length < 6) ? 0.5 : 1,
                  letterSpacing: 0.5, transition: "opacity 0.2s",
                }}>{loading ? "..." : t(lang, "twofa_verify_button")}</button>
              </form>

              <div style={{ textAlign: "center", marginTop: 24, fontSize: 14, color: "#999" }}>
                <span onClick={() => { setPendingToken(""); setCode(""); setError(""); }} style={{ color: "#0a0a0a", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                  {t(lang, "cancel")}
                </span>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ fontSize: isMobile ? 24 : 28, fontWeight: 800, marginBottom: 6, color: "#0a0a0a", marginTop: isMobile ? 20 : 0 }}>{t(lang, "login_title")}</h3>
              <p style={{ color: "#999", marginBottom: 28, fontSize: 14 }}>{t(lang, "login_sub")}</p>

              {error && (
                <div style={{ backgroundColor: "#F5F5F5", border: "1px solid #E0E0E0", borderRadius: 12, padding: "12px 16px", color: "#0a0a0a", fontSize: 13, marginBottom: 20, fontWeight: 600 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>{t(lang, "email")}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t(lang, "email")}
                    onFocus={onFieldFocus} onBlur={onFieldBlur} style={fieldStyle} />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#999", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>{t(lang, "password")}</label>
                  <div style={{ position: "relative" }}>
                    <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                      onFocus={onFieldFocus} onBlur={onFieldBlur} style={{ ...fieldStyle, paddingRight: 48 }} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", border: "none", backgroundColor: "transparent", cursor: "pointer", padding: 0, fontSize: 12, fontWeight: 700, color: "#999", letterSpacing: 0.5 }}>
                      {(showPassword ? t(lang, "hide") : t(lang, "show")).toUpperCase()}
                    </button>
                  </div>
                </div>

                <div style={{ textAlign: "right", marginBottom: 24 }}>
                  <span onClick={() => setShowForgot(true)} style={{ color: "#0a0a0a", fontSize: 13, cursor: "pointer", fontWeight: 700, textDecoration: "underline" }}>{t(lang, "forgot") || "Mot de passe oublié ?"}</span>
                </div>

                <button type="submit" disabled={loading} style={{
                  width: "100%", backgroundColor: "#0a0a0a", color: "white",
                  borderRadius: 14, padding: "15px", fontSize: 15, fontWeight: 700,
                  border: "none", cursor: "pointer", opacity: loading ? 0.7 : 1,
                  letterSpacing: 0.5, transition: "opacity 0.2s",
                }}>{loading ? "..." : t(lang, "login_btn")}</button>
              </form>

              {/* Le séparateur « ou » fait partie du composant : il n'a de sens que si les
                  boutons des fournisseurs sont affichés juste en dessous. */}
              <SocialAuthButtons onError={setError} />

              <div style={{ textAlign: "center", marginTop: 24, fontSize: 14, color: "#999" }}>
                {t(lang, "no_account")}{" "}
                <span onClick={() => navigate("/register")} style={{ color: "#0a0a0a", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                  {t(lang, "register_link")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
